import { lazy, Suspense, useEffect } from 'react';
import PageNavbar from '../../components/feature/PageNavbar';
import Footer from '../home/components/Footer';
import ScrollToTopButton from '../../components/feature/ScrollToTopButton';
import {
  useSEO,
  generateWebPageSchema,
  generateServiceSchema,
  generateBreadcrumbSchema,
  generateLocalBusinessSchema,
} from '../../utils/seo';
import Hero from './components/Hero';

const ServiceDetail = lazy(() => import('./components/ServiceDetail'));
const ServiceProcess = lazy(() => import('./components/ServiceProcess'));
const ServiceCTA = lazy(() => import('./components/ServiceCTA'));

const SectionLoader = () => (
  <div className="py-16 flex items-center justify-center">
    <div className="w-8 h-8 border-4 border-brand-green border-t-transparent rounded-full animate-spin" />
  </div>
);

export default function ServicesPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useSEO({
    title: 'Webdesign Titisee-Neustadt | AI Chatbot & SEO Optimierung Schwarzwald | BR Webdesign',
    description:
      'Webdesign, AI Chatbots & SEO in Titisee-Neustadt: Neue Webseiten, Redesign, Performance & lokale Sichtbarkeit für Unternehmen im Schwarzwald.',
    keywords:
      'Webdesign Titisee-Neustadt, AI Chatbot Schwarzwald, SEO Optimierung Baden-Württemberg, Website erstellen Hochschwarzwald, Webseite Redesign Freiburg, Performance Optimierung, lokale SEO, Wartung Support, responsive Design, KI Integration',
    canonical: '/services',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateWebPageSchema(
          'Webdesign Titisee-Neustadt | AI Chatbot & SEO Optimierung Schwarzwald',
          'Professionelle Webdesign-Leistungen in Titisee-Neustadt: Neue Webseiten, Premium Redesign, Performance-Optimierung, AI Chatbots, SEO & lokale Sichtbarkeit, Wartung & Support.',
          '/services'
        ),
        generateBreadcrumbSchema([
          { name: 'Startseite', url: '/' },
          { name: 'Leistungen', url: '/services' },
        ]),
        generateLocalBusinessSchema(),
        {
          '@type': 'Service',
          '@id': `${siteUrl}/services#webdesign`,
          name: 'Webdesign Titisee-Neustadt',
          description:
            'Professionelle Webseiten-Erstellung mit modernem Design, responsivem Layout und optimaler Performance für Unternehmen im Schwarzwald.',
          provider: {
            '@type': 'LocalBusiness',
            name: 'BR Webdesign & AI Solutions',
            address: {
              '@type': 'PostalAddress',
              addressLocality: 'Titisee-Neustadt',
              addressRegion: 'Baden-Württemberg',
              addressCountry: 'DE',
            },
            telephone: '+49-162-3651571',
            url: siteUrl,
          },
          areaServed: {
            '@type': 'GeoCircle',
            geoMidpoint: {
              '@type': 'GeoCoordinates',
              latitude: 47.9133,
              longitude: 8.2064,
            },
            geoRadius: '50000',
          },
          serviceType: 'Webdesign, AI Chatbot Integration, SEO Optimierung',
        },
        {
          '@type': 'Service',
          '@id': `${siteUrl}/services#ai-chatbot`,
          name: 'AI Chatbot Integration Schwarzwald',
          description:
            'Intelligente KI-Chatbots für automatisierten Kundensupport, Lead-Generierung und 24/7 Verfügbarkeit. Maßgeschneiderte AI-Lösungen für Ihr Unternehmen.',
          provider: {
            '@type': 'Organization',
            name: 'BR Webdesign & AI Solutions',
            url: siteUrl,
          },
        },
        {
          '@type': 'Service',
          '@id': `${siteUrl}/services#seo`,
          name: 'SEO Optimierung Baden-Württemberg',
          description:
            'Lokale und überregionale Suchmaschinenoptimierung für bessere Rankings bei Google. Technisches SEO, Content-Optimierung und Keyword-Strategie.',
          provider: {
            '@type': 'Organization',
            name: 'BR Webdesign & AI Solutions',
            url: siteUrl,
          },
          areaServed: {
            '@type': 'State',
            name: 'Baden-Württemberg',
          },
        },
      ],
    },
  });

  return (
    <div className="min-h-screen bg-white">
      <PageNavbar />
      <main>
        <Hero />
        <Suspense fallback={<SectionLoader />}>
          <ServiceDetail />
        </Suspense>
        <Suspense fallback={<SectionLoader />}>
          <ServiceProcess />
        </Suspense>
        <Suspense fallback={<SectionLoader />}>
          <ServiceCTA />
        </Suspense>
      </main>
      <Footer />
      <ScrollToTopButton />
    </div>
  );
}
